import { now } from '@seen/core/utils'
import type { ReactNode } from 'react'
import { create } from 'zustand'
import { combine, devtools } from 'zustand/middleware'

type ConfirmWithAnnotation = {
  isOpen: boolean
  title: string
  dateLabel: string
  description: ReactNode
  onConfirm: (note: string, date?: string) => void
  note: string
  isRichText: boolean
  includeDate: boolean
  date: string
  noteRequired: boolean
  noteLabel: string
  defaultNote: string
  onDateChange?: (args: {
    date: string
    note: string
    hasUserEditedNote: boolean
  }) => string
  hasUserEditedNote: boolean
  pastDateWarning?: ReactNode
}

const initialState: ConfirmWithAnnotation = {
  isOpen: false,
  title: '',
  dateLabel: 'As of Date',
  description: undefined,
  onConfirm: (_note: string, _date?: string) => {},
  note: '',
  isRichText: false,
  includeDate: false,
  date: now(),
  noteRequired: false,
  noteLabel: 'Note',
  defaultNote: '',
  onDateChange: undefined,
  hasUserEditedNote: false,
  pastDateWarning: undefined,
}

export const useConfirmWithAnnotationStore = create(
  devtools(
    combine(initialState, (set) => ({
      open: ({
        title,
        dateLabel,
        description,
        onConfirm,
        isRichText = false,
        includeDate = false,
        defaultDate = now(),
        noteRequired = false,
        noteLabel = 'Note',
        defaultNote = '',
        onDateChange,
        pastDateWarning,
      }: {
        title: string
        description: ReactNode
        dateLabel?: string
        onConfirm: (note: string, date?: string) => void
        isRichText?: boolean
        includeDate?: boolean
        defaultDate?: string
        noteRequired?: boolean
        noteLabel?: string
        defaultNote?: string
        onDateChange?: (args: {
          date: string
          note: string
          hasUserEditedNote: boolean
        }) => string
        pastDateWarning?: ReactNode
      }) =>
        // wrapping in requestAnimationFrame to ensure the state is set after the DOM is updated
        // This has been necessary to prevent an invisible overlay that prevents page interaction after closing the dialog
        requestAnimationFrame(() => {
          set({
            isOpen: true,
            title,
            dateLabel,
            description,
            onConfirm,
            note: defaultNote,
            isRichText,
            includeDate,
            date: defaultDate,
            noteRequired,
            noteLabel,
            defaultNote,
            onDateChange,
            hasUserEditedNote: false,
            pastDateWarning,
          })
        }),
      setIsOpen: (isOpen: boolean) =>
        set((state) => {
          if (!isOpen) {
            return initialState
          }
          return { ...state, isOpen }
        }),
      setNote: (note: string) =>
        set((state) => ({
          ...state,
          note,
          hasUserEditedNote:
            note !== state.defaultNote || state.hasUserEditedNote,
        })),
      setDate: (date: string) =>
        set((state) => {
          if (state.onDateChange) {
            const updatedNote = state.onDateChange({
              date,
              note: state.note,
              hasUserEditedNote: false,
            })
            return { ...state, date, note: updatedNote }
          }
          return { ...state, date }
        }),
      setIsRichText: (isRichText: boolean) =>
        set((state) => ({ ...state, isRichText })),
      reset: () => set(initialState),
    }))
  )
)
